import type { Job, PageJob } from "@istari/istari-client";
import type { InfiniteData, Query, QueryKey } from "@tanstack/react-query";

import { QUERY_REFETCH_INTERVAL_MS_60S } from "~/constants";
import { getLatestJobStatusName, isJobOpen } from "~/jobs/model/job-status";

type PageJobData = PageJob | undefined;
type OptionalInfinitePageJobData = InfiniteData<PageJobData, number>;

export const JOB_STATUS_ACTIVE_REFETCH_INTERVAL_MS = 10_000;
// Keep quiet pages lightly refreshed so newly-created jobs can appear without a manual reload.
export const JOB_STATUS_IDLE_REFETCH_INTERVAL_MS = QUERY_REFETCH_INTERVAL_MS_60S;

export function pageHasOpenJob(page: PageJobData) {
  return page?.items?.some((job) => isJobOpen(getLatestJobStatusName(job.statusHistory))) ?? false;
}

export function getJobPagePollingInterval(page: PageJobData) {
  return pageHasOpenJob(page) ? JOB_STATUS_ACTIVE_REFETCH_INTERVAL_MS : JOB_STATUS_IDLE_REFETCH_INTERVAL_MS;
}

export function getInfiniteJobPollingInterval(data: OptionalInfinitePageJobData | undefined) {
  return data?.pages.some(pageHasOpenJob) ? JOB_STATUS_ACTIVE_REFETCH_INTERVAL_MS : JOB_STATUS_IDLE_REFETCH_INTERVAL_MS;
}

export function adaptiveJobPageRefetchInterval<TPage extends PageJobData>(query: Query<TPage, Error, TPage, QueryKey>) {
  return getJobPagePollingInterval(query.state.data);
}

export function adaptiveJobInfiniteRefetchInterval<TData extends OptionalInfinitePageJobData | undefined>(
  query: Query<PageJob, Error, TData, QueryKey>,
) {
  return getInfiniteJobPollingInterval(query.state.data);
}

export function adaptiveJobRefetchInterval(query: Query<Job, Error, Job, QueryKey>) {
  const status = getLatestJobStatusName(query.state.data?.statusHistory);
  // A terminal job never changes again, so stop polling rather than idling on an interval.
  return isJobOpen(status) ? JOB_STATUS_ACTIVE_REFETCH_INTERVAL_MS : (false as const);
}

export type AdaptiveJobRefetchInterval = typeof adaptiveJobRefetchInterval;
export type AdaptiveJobPageRefetchInterval = typeof adaptiveJobPageRefetchInterval;
export type AdaptiveJobInfiniteRefetchInterval = typeof adaptiveJobInfiniteRefetchInterval;
